import React from 'react';

import * as Images from 'pos/Images';
import * as UI from 'core/ui';
import { VStack, HStack, Box, Text, Table } from '@chakra-ui/react';
import { CustomerDocumentIdType, CustomerForEditionDTO, CustomerOfferDTO, CustomerTypeInListDTO, OfferInListDTO, PriceListInListDTO, Res } from 'core/api';
import { CountryNames } from 'core/infrastructure';
import { zDecimal } from 'core/infrastructure-next';
import { CardPanel, SvgIcon } from 'core/ui-next';
import { FieldProps } from 'core/ui-next/chakra-components';
import { useControlsFor } from 'core/ui-next/controls';
import { isPhone } from 'pos/ui/ThemeManager';
import { z } from 'zod';


// ---------------------------------------------------------------------------------------------------------------------
// Esquema del formulario
// ---------------------------------------------------------------------------------------------------------------------
// El esquema replica, campo a campo, las propiedades del CustomerForEditionDTO que la antigua clase editaba con el
// UI.Form (linkTo => x.FiscalName, x => x.Address.Street, ...). Mantener los mismos nombres que el DTO es importante
// porque el servidor (CustomerFacade.validate) devuelve los errores de validación con el nombre de la propiedad del DTO,
// y así el Form puede asignarlos directamente al campo correspondiente.
//
// Los campos que son referencias a otras entidades (Type, PriceList, Offers) se declaran con z.custom para no tener que
// duplicar aquí toda la forma del DTO: no se validan en cliente (igual que antes), sólo viajan en el estado.
export const customerFormSchema = z.object({

	// Datos administrativos
	FiscalName: z.string(),
	BusinessName: z.string(),
	CountryCode: z.string(),
	// No usamos z.nativeEnum porque CustomerDocumentIdType es un enum con namespace (tiene su propio toString)
	DocumentIdType: z.number().transform(value => value as CustomerDocumentIdType),
	Cif: z.string(),
	AccountCode: z.string(),
	Type: z.custom<CustomerTypeInListDTO>().nullable(),

	// Contacto
	ContactPerson: z.string(),
	Telephone: z.string(),
	Email: z.string(),
	SendMailing: z.boolean(),

	// Ubicación
	Address: z.object({
		Street: z.string(),
		City: z.string(),
		Region: z.string(),
		ZipCode: z.string()
	}),

	// Condiciones comerciales
	CardNumber: z.string(),
	PriceList: z.custom<PriceListInListDTO>().nullable(),
	// La antigua clase usaba un DecimalBox con precision={2} allowNegative={false} required
	DiscountPercentage: zDecimal({ decimalPlaces: 2, nonNegative: true, required: true }),
	ApplySurcharge: z.boolean(),

	// Promociones
	Offers: z.array(z.custom<CustomerOfferDTO>()),

	// Observaciones
	Notes: z.string(),
	ShowNotes: z.boolean()
});

export type CustomerFormState = z.infer<typeof customerFormSchema>;
export type CustomerFormControls = ReturnType<typeof useControlsFor<CustomerFormState>>;


// ---------------------------------------------------------------------------------------------------------------------
// Mapeo entre el DTO y el estado del formulario
// ---------------------------------------------------------------------------------------------------------------------
export function toCustomerFormState(customer: CustomerForEditionDTO): CustomerFormState {
	return {
		FiscalName: customer.FiscalName ?? '',
		BusinessName: customer.BusinessName ?? '',
		CountryCode: customer.CountryCode ?? '',
		DocumentIdType: customer.DocumentIdType,
		Cif: customer.Cif ?? '',
		AccountCode: customer.AccountCode ?? '',
		Type: customer.Type ?? null,

		ContactPerson: customer.ContactPerson ?? '',
		Telephone: customer.Telephone ?? '',
		Email: customer.Email ?? '',
		SendMailing: customer.SendMailing ?? false,

		Address: {
			Street: customer.Address?.Street ?? '',
			City: customer.Address?.City ?? '',
			Region: customer.Address?.Region ?? '',
			ZipCode: customer.Address?.ZipCode ?? ''
		},

		CardNumber: customer.CardNumber ?? '',
		PriceList: customer.PriceList ?? null,
		DiscountPercentage: customer.DiscountPercentage ?? 0,
		ApplySurcharge: customer.ApplySurcharge ?? false,

		Offers: customer.Offers ?? [],

		Notes: customer.Notes ?? '',
		ShowNotes: customer.ShowNotes ?? false
	};
}

// Volcamos el estado del formulario sobre el cliente original. Partimos del customer recibido para no perder las
// propiedades que esta pantalla no edita (DeliveryAddresses, SpecialPrices, ParentCustomer, ValidPosGroups,
// las listas Existing*, ...), tal y como hacía la antigua clase al hacer un spread de su state.
export function toCustomerForEdition(customer: CustomerForEditionDTO, state: CustomerFormState): CustomerForEditionDTO {
	return {
		...customer,

		FiscalName: state.FiscalName,
		BusinessName: state.BusinessName,
		CountryCode: state.CountryCode,
		DocumentIdType: state.DocumentIdType,
		Cif: state.Cif,
		AccountCode: state.AccountCode,
		Type: state.Type!,

		ContactPerson: state.ContactPerson,
		Telephone: state.Telephone,
		Email: state.Email,
		SendMailing: state.SendMailing,

		Address: { ...customer.Address, ...state.Address },

		CardNumber: state.CardNumber,
		PriceList: state.PriceList!,
		DiscountPercentage: state.DiscountPercentage,
		ApplySurcharge: state.ApplySurcharge,

		Offers: state.Offers,

		Notes: state.Notes,
		ShowNotes: state.ShowNotes
	};
}


// ---------------------------------------------------------------------------------------------------------------------
// Utilidades de maquetación
// ---------------------------------------------------------------------------------------------------------------------
// El Field de los controles sólo pinta el mensaje de validación cuando hay error, así que el campo cambia de alto y
// desalinea la fila (y todo lo que va debajo). Para que las filas estén siempre al mismo nivel, se muestre o no el
// mensaje, reservamos su hueco con un padding inferior y sacamos el texto del flujo: el Field ya es position relative,
// así que basta con pintar el mensaje pegado a su parte inferior.
// Los mensajes que no caben en una línea se recortan, porque una segunda línea volvería a mover la fila.
const validationMessageSpace: FieldProps = {
	paddingBottom: '4.5',
	css: {
		// Se seleccionan la clase de Chakra y el data-part de Ark, para no depender de una sola de las dos
		'& .chakra-field__errorText, & [data-part="error-text"]': {
			maxWidth: 'full',
			display: 'block'
		}
	}
};

// Alturas de los paneles en TPV (desktop). Son alturas mínimas: las dos tarjetas de una misma fila se igualan entre
// ellas (la fila las estira) y, si el contenido necesita más sitio, el panel crece en lugar de hacer scroll interno.
// En Phone/comandera los paneles crecen con su contenido porque el contenedor exterior ya aporta el scroll vertical.
const ADMINISTRATIVE_PANEL_HEIGHT = '80';
const CONTACT_PANEL_HEIGHT = '80';
const LOCATION_PANEL_HEIGHT = '56';
const COMMERCIAL_TERMS_PANEL_HEIGHT = '56';
const PROMOTIONS_PANEL_HEIGHT = '63';
const NOTES_PANEL_HEIGHT = '63';

type PanelParams = {
	title: string;
	icon: React.ComponentProps<typeof SvgIcon>['src'];
	width: string;
	// Alto mínimo del panel en TPV/desktop. Si el contenido necesita más sitio (por ejemplo al aparecer un mensaje de
	// validación) el panel crece y la tarjeta de al lado se estira con él, así nunca aparece scroll dentro del panel.
	height: string;
	// Alto máximo en TPV/desktop. Sólo lo usan los paneles cuyo contenido no está acotado (la tabla de promociones),
	// que sí deben hacer scroll interno en lugar de crecer sin límite.
	maxHeight?: string;
	actions?: React.ReactNode;
	children: React.ReactNode;
}

// Marco común de todos los paneles: cabecera con icono + título (y acciones opcionales a la derecha) y el cuerpo.
function panel({ title, icon, width, height, maxHeight, actions, children }: PanelParams): React.ReactNode {
	const phone = isPhone();

	return (
		<CardPanel
			w={phone ? '100%' : width}
			h={'auto'}
			minH={phone ? 'auto' : height}
			maxH={phone || maxHeight === undefined ? 'none' : maxHeight}
			background={'card'}
			shadow={'md'}
			justifyContent={'flex-start'}
			alignItems={'stretch'}
		>
			<VStack width={'full'} height={phone ? 'auto' : 'full'} gap={'4'} alignItems={'stretch'} minH={'0'}>
				<HStack width={'full'} gap={'2'} alignItems={'center'} justifyContent={'space-between'}>
					<HStack gap={'2'} alignItems={'center'}>
						<SvgIcon src={icon} width={'4.5'} height={'4.5'} />
						<Text fontSize={'md'} fontWeight={'semibold'}>{title}</Text>
					</HStack>
					{actions}
				</HStack>
				<Box width={'full'} flex={phone ? undefined : '1'} minH={'0'} overflowY={'auto'} paddingRight={'1'}>
					{children}
				</Box>
			</VStack>
		</CardPanel>
	);
}

// Campo con un ancho concreto para TPV/desktop. Se usa cuando los campos de una fila no deben repartirse el ancho a
// partes iguales, por ejemplo:
//
//   fields: [
//       { field: <Select linkTo={'CountryCode'} ... />, width: '50%' },
//       { field: <Select linkTo={'DocumentIdType'} ... />, width: '25%' },
//       { field: <Input linkTo={'Cif'} ... />, width: '25%' }
//   ]
//
// El ancho sólo se aplica en TPV/desktop: en Phone cada campo ocupa la línea completa (o reparte a partes iguales si
// la fila es groupOnPhone).
type FieldWithWidth = {
	field: React.ReactNode;
	// Ancho del Box del campo en TPV/desktop. Se espera un porcentaje ('50%'), pero admite cualquier valor de ancho
	// de Chakra. Los campos de una misma fila deberían sumar 100%.
	width?: string;
}

// Un campo de una fila se puede pasar tal cual (reparte el ancho a partes iguales con el resto) o envuelto en un
// FieldWithWidth para fijarle el ancho.
type RowField = React.ReactNode | FieldWithWidth;

type FieldRow = {
	fields: RowField[];
	// Por defecto en Phone cada campo ocupa una línea completa. Con groupOnPhone dejamos los campos de la fila en
	// columnas también en Phone (por ejemplo Tipo de documento + CIF/NIF, tal y como aparece en el diseño).
	groupOnPhone?: boolean;
}

const isFieldWithWidth = (field: RowField): field is FieldWithWidth =>
	field !== null && typeof field === 'object' && 'field' in field;

function fieldRows(rows: FieldRow[]): React.ReactNode {
	const phone = isPhone();

	return (
		<VStack width={'full'} gap={'3'} alignItems={'stretch'}>
			{rows.map((row, index) => {

				// Normalizamos los campos al mismo formato (control + ancho opcional) y filtramos los que no se pintan
				// (por ejemplo el tipo de documento cuando el país no es el nuestro)
				const fields = row.fields
					.map<FieldWithWidth>(field => isFieldWithWidth(field) ? field : { field })
					.filter(({ field }) => field !== null && field !== undefined && field !== false);

				if (fields.length === 0)
					return null;

				return phone && !row.groupOnPhone
					? <VStack key={index} width={'full'} gap={'3'} alignItems={'stretch'}>
						{fields.map(({ field }, fieldIndex) => <Box key={fieldIndex} width={'full'}>{field}</Box>)}
					</VStack>
					: <HStack key={index} width={'full'} gap={'4'} alignItems={'flex-start'}>
						{fields.map(({ field, width }, fieldIndex) => {

							// En Phone ignoramos el ancho configurado: los campos agrupados siguen repartiéndose a partes iguales
							const fieldWidth = phone ? undefined : width;

							// Con ancho fijo el Box no crece, pero sí puede encogerse para que el gap entre campos no
							// desborde la fila cuando los anchos suman el 100%
							return <Box key={fieldIndex} width={fieldWidth ?? 'full'} flex={fieldWidth ? '0 1 auto' : '1'} minW={'0'} alignItems={'flex-end'} height={'stretch'} display={'flex'}>{field}</Box>;
						})}
					</HStack>;
			})}
		</VStack>
	);
}

// Botón de acción de la cabecera del panel de promociones (icono enmarcado + etiqueta debajo), con el mismo estilo
// que los botones de la cabecera de la pantalla de ver cliente asignado.
function panelActionButton(label: string, icon: React.ComponentProps<typeof SvgIcon>['src'], onClick: () => void, disabled = false): React.ReactNode {
	return (
		<VStack
			width={'12'}
			gapY={'1'}
			cursor={disabled ? 'default' : 'pointer'}
			opacity={disabled ? 0.4 : 1}
			onClick={disabled ? undefined : onClick}
		>
			<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
				<SvgIcon src={icon} width={'5'} height={'5'} />
			</Box>
			<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{label}</Text>
		</VStack>
	);
}

// Opción informativa que se muestra cuando no queda ningun tipo de cliente disponible.
// El disabled no es del DTO: es la marca con la que la colección del Select pinta la opción sin dejar
// seleccionarla.
const notAvailableCustomerTypeOption = (): CustomerTypeInListDTO =>
	({ Name: Res.Web.ADD_OR_EDIT_CUSTOMER_NOT_AVAILABLE_CUSTOMER_TYPES_LBL(), disabled: true } as unknown as CustomerTypeInListDTO);


// ---------------------------------------------------------------------------------------------------------------------
// Paneles
// ---------------------------------------------------------------------------------------------------------------------
export type AdministrativePanelParams = {
	controls: CustomerFormControls;
	customer: CustomerForEditionDTO;
	// El tipo de documento sólo se pide cuando el país es el nuestro, igual que en la antigua clase
	showDocumentIdType: boolean;
	onCountryChanged: (countryCode: string) => void;
}

export function administrativePanel({ controls, customer, showDocumentIdType, onCountryChanged }: AdministrativePanelParams): React.ReactNode {

	const { Input, Select } = controls;

	const countrySelect = <Select
		key={'CountryCode'}
		linkTo={'CountryCode'}
		fieldProps={validationMessageSpace}
		label={Res.Web.ADD_OR_EDIT_CUSTOMER_COUNTRY_LBL()}
		placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_COUNTRY_PLACEHOLDER()}
		options={customer.ExistingCountryCodes}
		renderOption={option => CountryNames.getName(option ?? '')}
		valueGetter={option => option ?? ''}
		onWatch={onCountryChanged}
		required
	/>;

	const customerTypeOptions = customer.ExistingTypes.length === 0 ? [notAvailableCustomerTypeOption()] : customer.ExistingTypes;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_ADMINISTRATIVE_DATA_LBL(),
		icon: Images.User,
		width: '58%',
		height: ADMINISTRATIVE_PANEL_HEIGHT,
		children: fieldRows([
			{
				fields: [
					<Input
						key={'FiscalName'}
						linkTo={'FiscalName'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_FISCAL_NAME_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_FISCAL_NAME_PLACEHOLDER()}
						size={'sm'}
						required
					/>,
					<Input
						key={'BusinessName'}
						linkTo={'BusinessName'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_BUSINESS_NAME_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_BUSINESS_NAME_PLACEHOLDER()}
						size={'sm'}
					/>
				]
			},
			{
				groupOnPhone: true,
				fields: [
					isPhone()
						? countrySelect
						: null
				]
			},
			{
				groupOnPhone: true,
				fields: [
					!isPhone()
						? countrySelect
						: null
					,
					showDocumentIdType
						? <Select
							key={'DocumentIdType'}
							linkTo={'DocumentIdType'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_DOCUMENT_TYPE_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_DOCUMENT_TYPE_PLACEHOLDER()}
							options={customer.ExistingDocumentIdTypes}
							renderOption={option => CustomerDocumentIdType.toString(option) ?? ''}
							valueGetter={option => CustomerDocumentIdType.toString(option) ?? ''}
							required
						/>
						: null,
					<Input
						key={'Cif'}
						linkTo={'Cif'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_CIF_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_CIF_PLACEHOLDER()}
						size={'sm'}
					/>
				]
			},
			{
				fields: [
					{
						field: <Input
							key={'AccountCode'}
							linkTo={'AccountCode'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_ACCOUNT_CODE_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_ACCOUNT_CODE_PLACEHOLDER()}
							size={'sm'}
						/>, width: '55%'
					},
					{
						field: <Select
							key={'Type'}
							linkTo={'Type'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_TYPE_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_TYPE_PLACEHOLDER()}
							options={customerTypeOptions}
							renderOption={option => option?.Name ?? ''}
							valueGetter={option => option?.Name ?? ''}
						/>, width: '45%'
					},
				]
			}
		])
	});
}


export function contactPanel(controls: CustomerFormControls): React.ReactNode {

	const { Input, CheckBox } = controls;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_CONTACT_LBL(),
		icon: Images.Phone,
		width: '42%',
		height: CONTACT_PANEL_HEIGHT,
		children: fieldRows([
			{
				fields: [
					<Input
						key={'ContactPerson'}
						linkTo={'ContactPerson'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_CONTACT_PERSON_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_CONTACT_PERSON_PLACEHOLDER()}
						size={'sm'}
					/>,
					<Input
						key={'Telephone'}
						linkTo={'Telephone'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_TELEPHONE_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_TELEPHONE_PLACEHOLDER()}
						size={'sm'}
					/>
				]
			},
			{
				fields: [
					<Input
						key={'Email'}
						linkTo={'Email'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_EMAIL_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_EMAIL_PLACEHOLDER()}
						maxLength={400}
						size={'sm'}
					/>
				]
			},
			{
				fields: [
					<VStack key={'SendMailing'} height={isPhone() ? '12.5' : '16.25'} marginLeft={'1'}>
						<CheckBox
							key={'SendMailing'}
							linkTo={'SendMailing'}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_RECIEVE_ADVERTISING_CHK()}
							fieldProps={{ justifyContent: 'flex-end' }}
						/>
					</VStack >
				]
			}
		])
	});
}


export function locationPanel(controls: CustomerFormControls): React.ReactNode {

	const { Input } = controls;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_LOCATION_LBL(),
		icon: Images.Location,
		width: '58%',
		height: LOCATION_PANEL_HEIGHT,
		children: fieldRows([
			{
				fields: [
					{
						field: <Input
							key={'Street'}
							linkTo={'Address.Street'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_ADDRESS_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_ADDRESS_PLACEHOLDER()}
							size={'sm'}
						/>, width: '75%'
					},
					{
						field: <Input
							key={'ZipCode'}
							linkTo={'Address.ZipCode'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_ZIPCODE_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_ZIPCODE_PLACEHOLDER()}
							size={'sm'}
						/>, width: '25%'
					}
				]
			},
			{
				fields: [
					<Input
						key={'City'}
						linkTo={'Address.City'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_CITY_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_CITY_PLACEHOLDER()}
						size={'sm'}
					/>,
					<Input
						key={'Region'}
						linkTo={'Address.Region'}
						fieldProps={validationMessageSpace}
						label={Res.Web.ADD_OR_EDIT_CUSTOMER_REGION_LBL()}
						placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_REGION_PLACEHOLDER()}
						size={'sm'}
					/>
				]
			}
		])
	});
}


export function commercialTermsPanel(controls: CustomerFormControls, customer: CustomerForEditionDTO): React.ReactNode {

	const { Input, Select, DecimalInput, CheckBox } = controls;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_COMMERCIAL_TERMS_LBL(),
		icon: Images.Tag,
		width: '42%',
		height: COMMERCIAL_TERMS_PANEL_HEIGHT,
		children: fieldRows([
			{
				fields: [
					{
						field: <Input
							key={'CardNumber'}
							linkTo={'CardNumber'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_CARD_NUMBER_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_CARD_NUMBER_PLACEHOLDER()}
							size={'sm'}
						/>, width: '40%'
					},
					{
						field: <Select
							key={'PriceList'}
							linkTo={'PriceList'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_PRICE_LIST_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_PRICE_LIST_PLACEHOLDER()}
							options={customer.ExistingPriceLists}
							renderOption={option => option?.Name ?? ''}
							valueGetter={option => option?.Name ?? ''}
						/>, width: '60%'
					}
				]
			},
			{
				groupOnPhone: true,
				fields: [
					{
						field: <DecimalInput
							key={'DiscountPercentage'}
							linkTo={'DiscountPercentage'}
							fieldProps={validationMessageSpace}
							label={Res.Web.ADD_OR_EDIT_CUSTOMER_DISCOUNT_LBL()}
							placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_DISCOUNT_PLACEHOLDER()}
							precision={2}
							allowNegative={false}
							endElement={<Text fontSize={'sm'}>{'%'}</Text>}
							size={'sm'}
							required
						/>, width: '30%'
					},
					{
						field: <VStack key={'ApplySurcharge'} paddingBottom={'2'}>
							<CheckBox
								linkTo={'ApplySurcharge'}
								label={Res.Web.ADD_OR_EDIT_CUSTOMER_APPLY_SURCHARGE_CHK()}
								// Reserva el mismo hueco de mensaje que el Descuento, con el que comparte fila, para que ambos
								// controles sigan alineados aparezca o no el mensaje de validación
								fieldProps={{ ...validationMessageSpace, marginLeft: '1' }}
							/>
						</VStack>, width: '70%'
					}
				]
			}
		])
	});
}


export type PromotionsPanelParams = {
	offers: CustomerOfferDTO[];
	selectedOffer: Nullable<CustomerOfferDTO>;
	onSelectOffer: (offer: CustomerOfferDTO) => void;
	onNewOffer: () => void;
	onEditOffer: () => void;
	onDeleteOffer: () => void;
}

function promotionsActions({ selectedOffer, onNewOffer, onEditOffer, onDeleteOffer }: PromotionsPanelParams): React.ReactNode {
	const hasSelection = selectedOffer !== null;

	return (
		<HStack gap={'1.5'} alignItems={'flex-start'}>
			{panelActionButton(Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_DELETE_BTN(), Images.TrashCan, onDeleteOffer, !hasSelection)}
			{panelActionButton(Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_EDIT_BTN(), Images.Edit, onEditOffer, !hasSelection)}
			{panelActionButton(Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_NEW_BTN(), Images.Plus, onNewOffer)}
		</HStack>
	);
}

// La promoción seleccionada es siempre un elemento del propio array de promociones, así que comparamos por referencia
const selectedOfferBackground = (offer: CustomerOfferDTO, selectedOffer: Nullable<CustomerOfferDTO>) =>
	offer === selectedOffer ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;

const offerName = (offer: Nullable<OfferInListDTO>) => offer?.Name ?? '';


export function promotionsPanel(params: PromotionsPanelParams): React.ReactNode {
	const { offers, selectedOffer, onSelectOffer } = params;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_LBL(),
		icon: Images.Offer,
		width: '66%',
		height: PROMOTIONS_PANEL_HEIGHT,
		// La tabla puede tener tantas promociones como quiera, así que este panel sí se queda con su alto y hace scroll
		maxHeight: PROMOTIONS_PANEL_HEIGHT,
		actions: promotionsActions(params),
		children: offers.length === 0
			? <Box minH={'5'}><Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_NOT_DATA_LBL()}</Text></Box>
			: <Table.Root size={'sm'} striped interactive stickyHeader>
				<Table.Header>
					<Table.Row>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_ID_CLM()}</Text>
						</Table.ColumnHeader>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} textAlign={'start'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_NAME_CLM()}</Text>
						</Table.ColumnHeader>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} textAlign={'end'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_MAX_APPLICATIONS_CLM()}</Text>
						</Table.ColumnHeader>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} textAlign={'center'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_FROM_CLM()}</Text>
						</Table.ColumnHeader>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} textAlign={'center'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_TO_CLM()}</Text>
						</Table.ColumnHeader>
						<Table.ColumnHeader>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_SCHEDULE_CLM()}</Text>
						</Table.ColumnHeader>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{offers.map((customerOffer, index) => {
						const background = selectedOfferBackground(customerOffer, selectedOffer);

						return (
							<Table.Row key={index} onClick={() => onSelectOffer(customerOffer)} cursor={'pointer'}>
								<Table.Cell bg={background}><Text fontSize={'sm'}>{customerOffer.Offer?.Id ?? ''}</Text></Table.Cell>
								<Table.Cell bg={background}><Text textAlign={'start'} fontSize={'sm'}>{offerName(customerOffer.Offer)}</Text></Table.Cell>
								<Table.Cell bg={background}><Text textAlign={'end'} fontSize={'sm'}>{customerOffer.MaxApplicationsPerBusinessDay ?? ''}</Text></Table.Cell>
								<Table.Cell bg={background}><Text fontSize={'sm'}>{UI.Format.asDate(customerOffer.Offer?.From)}</Text></Table.Cell>
								<Table.Cell bg={background}><Text fontSize={'sm'}>{UI.Format.asDate(customerOffer.Offer?.To)}</Text></Table.Cell>
								<Table.Cell bg={background}><Text fontSize={'sm'}>{customerOffer.Offer?.ScheduleDescription ?? ''}</Text></Table.Cell>
							</Table.Row>
						);
					})}
				</Table.Body>
			</Table.Root>
	});
}


// En Phone/comandera la tabla de promociones se reduce a dos columnas y el resto de datos se apila debajo del
// nombre y de la programación, igual que en la pantalla de ver cliente asignado.
export function promotionsPhonePanel(params: PromotionsPanelParams): React.ReactNode {
	const { offers, selectedOffer, onSelectOffer } = params;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_LBL(),
		icon: Images.Offer,
		width: '100%',
		height: PROMOTIONS_PANEL_HEIGHT,
		actions: promotionsActions(params),
		// El panel crece con su contenido hasta el alto máximo de la tabla, que a partir de ahí hace su propio scroll
		children: offers.length === 0
			? <Box minH={'9'}><Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_NOT_DATA_LBL()}</Text></Box>
			// Las dos columnas reparten el ancho del panel a partes iguales (tableLayout fixed + 50%), igual que en la
			// pantalla de ver cliente asignado: los nombres largos parten a otra línea en lugar de ensanchar su columna.
			// El scroll vertical lo da este Box (y no un TableScrollArea, cuyo estilo base lleva un white-space: nowrap
			// que heredarían las celdas, con lo que los textos se saldrían de su columna en lugar de partirse).
			: <Box width={'full'} maxH={'103'} overflowY={'auto'}>
				<Table.Root size={'sm'} striped interactive stickyHeader tableLayout={'fixed'} width={'full'} padding={'0'}>
					<Table.Header>
						<Table.Row>
							<Table.ColumnHeader w={'50%'}>
								<Text fontSize={'sm'} textAlign={'start'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_NAME_CLM()}</Text>
							</Table.ColumnHeader>
							<Table.ColumnHeader w={'50%'}>
								<Text fontSize={'sm'} textAlign={'end'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_SCHEDULE_CLM()}</Text>
							</Table.ColumnHeader>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{offers.map((customerOffer, index) => {
							const background = selectedOfferBackground(customerOffer, selectedOffer);

							return (
								<Table.Row key={index} onClick={() => onSelectOffer(customerOffer)} cursor={'pointer'}>
									<Table.Cell w={'50%'} bg={background} verticalAlign={'top'}>
										<Box display={'flex'} flexDirection={'column'} gap={'2'}>
											<Box><Text textAlign={'start'} fontSize={'sm'}>{offerName(customerOffer.Offer)}</Text></Box>
											{customerOffer.Offer?.Id
												? <Box><Text textAlign={'start'} fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_ID_CLM() + ' - ' + customerOffer.Offer.Id}</Text></Box>
												: <></>
											}
											{customerOffer.MaxApplicationsPerBusinessDay !== null && customerOffer.MaxApplicationsPerBusinessDay !== undefined
												? <Box><Text textAlign={'start'} fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_MAX_APPLICATIONS_CLM() + ' - ' + customerOffer.MaxApplicationsPerBusinessDay}</Text></Box>
												: <></>
											}
										</Box>
									</Table.Cell>
									<Table.Cell w={'50%'} bg={background} verticalAlign={'top'}>
										<Box display={'flex'} flexDirection={'column'} gap={'2'}>
											<Box><Text textAlign={'end'} fontSize={'sm'}>{customerOffer.Offer?.ScheduleDescription ?? ''}</Text></Box>
											{customerOffer.Offer?.From
												? <Box><Text textAlign={'end'} fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_FROM_CLM() + ' - ' + UI.Format.asDate(customerOffer.Offer.From)}</Text></Box>
												: <></>
											}
											{customerOffer.Offer?.To
												? <Box><Text textAlign={'end'} fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_TO_CLM() + ' - ' + UI.Format.asDate(customerOffer.Offer.To)}</Text></Box>
												: <></>
											}
										</Box>
									</Table.Cell>
								</Table.Row>
							);
						})}
					</Table.Body>
				</Table.Root>
			</Box>
	});
}


export function notesPanel(controls: CustomerFormControls): React.ReactNode {

	const { TextArea, CheckBox } = controls;

	return panel({
		title: Res.Web.ADD_OR_EDIT_CUSTOMER_NOTES_LBL(),
		icon: Images.Observations,
		width: '34%',
		height: NOTES_PANEL_HEIGHT,
		children: (
			<VStack width={'full'} gap={'3'} alignItems={'stretch'}>
				<TextArea
					linkTo={'Notes'}
					placeholder={Res.Web.ADD_OR_EDIT_CUSTOMER_NOTES_PLACEHOLDER()}
					rows={5}
					resize={'none'}
					bg={{ base: 'white', _dark: '{colors.neutral.800}' }}
					maxLength={800}
				/>
				<CheckBox
					linkTo={'ShowNotes'}
					label={Res.Web.ADD_OR_EDIT_CUSTOMER_SHOW_NOTES_CHK()}
					fieldProps={{ marginLeft: '1' }}
				/>
			</VStack>
		)
	});
}
